/* eslint-disable react/prop-types */
/* eslint-disable prettier/prettier */
import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React examples
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import PieChart from "examples/Charts/PieChart";
import MixedChart from "examples/Charts/MixedChart";

// Utilidades de exportación
import { exportDashboardToPDF, exportToExcel, handlePrint as printPage } from "utils/exportHelpers";

export default function DashboardGeneral() {
  const [periodo, setPeriodo] = useState("mes");
  const [empresa, setEmpresa] = useState("");
  const [sucursal, setSucursal] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para modales y notificaciones
  const [modalExportar, setModalExportar] = useState({ open: false, tipo: "" });
  const [exportando, setExportando] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, mensaje: "", severity: "success" });

  // Estados para datos de la API
  const [kpisGlobales, setKpisGlobales] = useState([]);
  const [evolucionFinanciera, setEvolucionFinanciera] = useState({ labels: [], datasets: [] });
  const [ingresosPorModulo, setIngresosPorModulo] = useState({ labels: [], datasets: [] });
  const [gastosPorCategoria, setGastosPorCategoria] = useState({ labels: [], datasets: [] });
  const [ventasPorSucursal, setVentasPorSucursal] = useState({ labels: [], datasets: [] });
  const [rotacionInventario, setRotacionInventario] = useState({ labels: [], datasets: [] });
  const [tendenciaClientes, setTendenciaClientes] = useState({ labels: [], datasets: [] });
  const [eficienciaOperativa, setEficienciaOperativa] = useState({ labels: [], datasets: [] });
  const [indicadoresFinancierosData, setIndicadoresFinancierosData] = useState({
    roe: { value: 0 },
    roa: { value: 0 },
    utilidad: { value: 0 },
    liquidez: { value: 0 },
    endeudamiento: { value: 0 },
  });

  // Cargar datos de la API
  useEffect(() => {
    const fetchDatos = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          periodo: periodo,
        };

        if (empresa) params.empresa_id = empresa;
        if (sucursal) params.sucursal_id = sucursal;

        console.log("📊 Fetching dashboard data with params:", params);
        const response = await axios.get(API_ENDPOINTS.REPORTES.DASHBOARD_GENERAL, { params });
        const data = response.data;

        console.log("✅ Dashboard data received:", data);
        console.log("💰 Ingresos totales:", data.kpis.ingresos_totales);
        console.log("🛒 Ventas cantidad:", data.kpis.ventas_cantidad);

        // Transformar KPIs
        const formatCurrency = (value) => {
          const formatted = new Intl.NumberFormat("es-MX", {
            notation: "compact",
            compactDisplay: "short",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(value);
          return `$${formatted}`;
        };

        const calcularCambio = (actual, anterior) => {
          if (!anterior || anterior === 0)
            return { color: "secondary", amount: "N/A", label: "sin datos previos" };
          const cambio = ((actual - anterior) / anterior) * 100;
          return {
            color: cambio >= 0 ? "success" : "error",
            amount: `${cambio >= 0 ? "+" : ""}${cambio.toFixed(1)}%`,
            label: "vs período anterior",
          };
        };

        setKpisGlobales([
          {
            color: "success",
            icon: "trending_up",
            title: "Ingresos Totales",
            count: formatCurrency(data.kpis.ingresos_totales),
            percentage: calcularCambio(data.kpis.ingresos_totales, data.kpis.ingresos_anteriores),
          },
          {
            color: "primary",
            icon: "account_balance",
            title: "Utilidad Neta",
            count: formatCurrency(data.kpis.utilidad_neta),
            percentage: {
              color: data.kpis.margen_utilidad >= 20 ? "success" : "warning",
              amount: `${data.kpis.margen_utilidad.toFixed(1)}%`,
              label: "margen de utilidad",
            },
          },
          {
            color: "info",
            icon: "inventory_2",
            title: "Valor Inventario",
            count: formatCurrency(data.kpis.valor_inventario),
            percentage: { color: "info", amount: "-", label: "inventario total" },
          },
          {
            color: "error",
            icon: "point_of_sale",
            title: "Ventas del Período",
            count: data.kpis.ventas_cantidad.toString(),
            percentage: {
              color: "info",
              amount: formatCurrency(data.kpis.ingresos_totales / (data.kpis.ventas_cantidad || 1)),
              label: "ticket promedio",
            },
          },
          {
            color: "dark",
            icon: "people",
            title: "Clientes Activos",
            count: data.kpis.clientes_activos.toString(),
            percentage: { color: "success", amount: "-", label: "en período" },
          },
        ]);

        // Transformar evolución financiera
        if (data.evolucion_financiera && data.evolucion_financiera.length > 0) {
          setEvolucionFinanciera({
            labels: data.evolucion_financiera.map((item) => item.mes),
            datasets: [
              {
                label: "Ingresos",
                color: "success",
                data: data.evolucion_financiera.map((item) => parseFloat(item.ingresos)),
              },
              {
                label: "Gastos",
                color: "error",
                data: data.evolucion_financiera.map((item) => parseFloat(item.gastos)),
              },
              {
                label: "Utilidad",
                color: "info",
                data: data.evolucion_financiera.map((item) => parseFloat(item.utilidad)),
              },
            ],
          });
        }

        // Transformar ingresos por módulo
        if (data.ingresos_por_modulo && data.ingresos_por_modulo.length > 0) {
          setIngresosPorModulo({
            labels: data.ingresos_por_modulo.map((item) => item.modulo),
            datasets: [{
              label: "Ingresos",
              backgroundColors: ["success", "info", "warning", "primary"],
              data: data.ingresos_por_modulo.map((item) => parseFloat(item.valor)),
            }],
          });
        }

        // Transformar gastos por categoría
        if (data.gastos_por_categoria && data.gastos_por_categoria.length > 0) {
          setGastosPorCategoria({
            labels: data.gastos_por_categoria.map((item) => item.categoria),
            datasets: [{
              label: "Gastos",
              backgroundColors: ["error", "warning", "info", "dark"],
              data: data.gastos_por_categoria.map((item) => parseFloat(item.valor)),
            }],
          });
        }

        // Transformar ventas por sucursal
        if (data.ventas_por_sucursal && data.ventas_por_sucursal.length > 0) {
          setVentasPorSucursal({
            labels: data.ventas_por_sucursal.map((item) => item.nombre),
            datasets: [
              {
                label: "Ventas",
                color: "primary",
                data: data.ventas_por_sucursal.map((item) => parseFloat(item.total)),
              },
            ],
          });
        }

        // Transformar indicadores financieros (ROE, ROA, etc)
        if (data.indicadores_financieros) {
          // Buscamos indicadores específicos por título
          const findIndicador = (titulo) =>
            data.indicadores_financieros.find((i) => i.title.includes(titulo)) || { value: 0 };

          setIndicadoresFinancierosData({
            roe: findIndicador("ROE"),
            roa: findIndicador("ROA"),
            utilidad: { value: data.kpis.margen_utilidad || 0 }, // Usamos el del KPI principal
            liquidez: { value: 2.4 }, // Valor estimado por ahora si no viene
            endeudamiento: { value: 42.8 }, // Valor estimado por ahora si no viene
          });
        }

        // Transformar rotación de inventario y eficiencia
        if (data.indicadores_operativos) {
          const rotacion = data.indicadores_operativos.find((i) =>
            i.title.includes("Rotación")
          ) || { value: 0 };
          const eficiencia = data.indicadores_operativos.find((i) =>
            i.title.includes("Eficiencia")
          ) || { value: 0 };

          setRotacionInventario({
            labels: ["Rotación Actual"],
            datasets: [
              {
                label: "Rotación (x)",
                color: "warning",
                data: [parseFloat(rotacion.value)],
              },
            ],
          });

          setEficienciaOperativa({
            labels: ["Eficiencia Global"],
            datasets: [
              {
                label: "Eficiencia (%)",
                color: "info",
                data: [parseFloat(eficiencia.value)],
              },
            ],
          });
        }

        // Tendencia de clientes (usar reales si vienen o estimar)
        const evolucionLabels =
          data.evolucion_financiera && data.evolucion_financiera.length > 0
            ? data.evolucion_financiera.map((item) => item.mes)
            : ["Sin datos"];

        setTendenciaClientes({
          labels: evolucionLabels,
          datasets: [
            {
              label: "Clientes Activos",
              color: "success",
              data:
                data.evolucion_financiera && data.evolucion_financiera.length > 0
                  ? data.evolucion_financiera.map((item) =>
                    item.clientes ? parseInt(item.clientes) : 0
                  )
                  : [0],
            },
          ],
        });
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setError(err.message || "Error al cargar los datos");
      } finally {
        setLoading(false);
      }
    };

    fetchDatos();
  }, [periodo, empresa, sucursal]);

  const abrirModalExportar = (tipo) => {
    console.log("🔵 Abriendo modal de exportación:", tipo);
    setModalExportar({ open: true, tipo });
  };

  const confirmarExportacion = async () => {
    const { tipo } = modalExportar;
    console.log("🟢 Confirmando exportación tipo:", tipo);
    setModalExportar({ open: false, tipo: "" });
    setExportando(true);

    try {
      if (tipo === "pdf") {
        console.log("📄 Iniciando exportación a PDF...");
        await exportDashboardToPDF(
          "dashboard-general-content",
          `dashboard-general-${periodo}-${new Date().getTime()}.pdf`
        );
        console.log("✅ PDF exportado exitosamente");
        setSnackbar({
          open: true,
          mensaje: "Dashboard exportado a PDF exitosamente",
          severity: "success",
        });
      } else if (tipo === "excel") {
        console.log("📊 Iniciando exportación a Excel...");
        // Preparar datos de KPIs para Excel
        const kpisData = kpisGlobales.map((kpi) => ({
          Métrica: kpi.title,
          Valor: kpi.count,
          Cambio: kpi.percentage.amount,
          Descripción: kpi.percentage.label,
        }));

        // Preparar datos de evolución financiera
        const evolucionData =
          evolucionFinanciera.labels && evolucionFinanciera.labels.length > 0
            ? evolucionFinanciera.labels.map((label, index) => ({
              Mes: label,
              Ingresos: `$${evolucionFinanciera.datasets[0].data[index].toLocaleString("es-MX", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
              Gastos: `$${evolucionFinanciera.datasets[1].data[index].toLocaleString("es-MX", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
              Utilidad: `$${evolucionFinanciera.datasets[2].data[index].toLocaleString("es-MX", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
            }))
            : [];

        // Preparar datos de ingresos por módulo
        const ingresosModuloData =
          ingresosPorModulo.labels && ingresosPorModulo.labels.length > 0
            ? ingresosPorModulo.labels.map((label, index) => ({
              Módulo: label,
              Ingresos: `$${ingresosPorModulo.datasets.data[index].toLocaleString("es-MX", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
            }))
            : [];

        // Exportar datos combinados
        const allData = [
          { Sección: "INDICADORES CLAVE DE RENDIMIENTO (KPIs)" },
          ...kpisData,
          {},
          { Sección: "EVOLUCIÓN FINANCIERA" },
          ...evolucionData,
          {},
          { Sección: "INGRESOS POR MÓDULO" },
          ...ingresosModuloData,
        ];

        exportToExcel(
          allData,
          "Dashboard General",
          `dashboard-general-${periodo}-${new Date().getTime()}.xlsx`
        );
        console.log("✅ Excel exportado exitosamente");
        setSnackbar({
          open: true,
          mensaje: "Datos exportados a Excel exitosamente",
          severity: "success",
        });
      } else if (tipo === "print") {
        console.log("🖨️ Iniciando impresión...");
        printPage();
        setSnackbar({
          open: true,
          mensaje: "Preparando impresión...",
          severity: "info",
        });
      }
    } catch (error) {
      console.error("❌ Error al exportar:", error);
      setSnackbar({
        open: true,
        mensaje: `Error al exportar: ${error.message}`,
        severity: "error",
      });
    } finally {
      console.log("🔄 Finalizando exportación");
      setExportando(false);
    }
  };

  return (
    <MDBox id="dashboard-general-content">
      {/* Filtros Globales */}
      <Card sx={{ mb: 3 }}>
        <MDBox p={3}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Período"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                fullWidth
              >
                <MenuItem value="dia">Hoy</MenuItem>
                <MenuItem value="semana">Esta Semana</MenuItem>
                <MenuItem value="mes">Este Mes</MenuItem>
                <MenuItem value="trimestre">Este Trimestre</MenuItem>
                <MenuItem value="semestre">Este Semestre</MenuItem>
                <MenuItem value="anio">Este Año</MenuItem>
                <MenuItem value="personalizado">Personalizado</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Empresa"
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                fullWidth
                disabled={loading}
              >
                <MenuItem value="">Todas las Empresas</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                fullWidth
                disabled={loading}
              >
                <MenuItem value="">Todas las Sucursales</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDBox display="flex" gap={1} flexWrap="wrap">
                <MDButton
                  variant="gradient"
                  color="error"
                  size="small"
                  onClick={() => abrirModalExportar("pdf")}
                  disabled={loading || exportando}
                  sx={{ flex: "1 1 auto", minWidth: "90px" }}
                >
                  <Icon sx={{ mr: 0.5 }}>picture_as_pdf</Icon>
                  PDF
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="success"
                  size="small"
                  onClick={() => abrirModalExportar("excel")}
                  disabled={loading || exportando}
                  sx={{ flex: "1 1 auto", minWidth: "90px" }}
                >
                  <Icon sx={{ mr: 0.5 }}>table_chart</Icon>
                  Excel
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={() => abrirModalExportar("print")}
                  disabled={loading || exportando}
                  sx={{ minWidth: "45px", px: 1 }}
                >
                  <Icon>print</Icon>
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      {/* Indicador de Carga */}
      {loading && (
        <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress color="info" size={60} />
        </MDBox>
      )}

      {/* Mensaje de Error */}
      {error && (
        <Card sx={{ mb: 3, bgcolor: "error.main", color: "white" }}>
          <MDBox p={2}>
            <MDTypography variant="h6" color="white">
              <Icon>error_outline</Icon> Error al cargar datos
            </MDTypography>
            <MDTypography variant="body2" color="white">
              {error}
            </MDTypography>
          </MDBox>
        </Card>
      )}

      {/* Contenido del Dashboard (solo si no está cargando y no hay error) */}
      {!loading && !error && (
        <>
          {/* KPIs Principales */}
          <Grid container spacing={3} mb={3}>
            {kpisGlobales.map((kpi, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <ComplexStatisticsCard
                  color={kpi.color}
                  icon={kpi.icon}
                  title={kpi.title}
                  count={kpi.count}
                  percentage={kpi.percentage}
                />
              </Grid>
            ))}
          </Grid>

          {/* Indicadores Financieros Adicionales */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={3}>
              <Card>
                <MDBox p={2} textAlign="center">
                  <MDTypography variant="h6" fontWeight="medium" color="dark">
                    Margen de Utilidad
                  </MDTypography>
                  <MDTypography variant="h3" fontWeight="bold" color="success">
                    {indicadoresFinancierosData.utilidad.value.toFixed(1)}%
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Sobre ventas totales
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <MDBox p={2} textAlign="center">
                  <MDTypography variant="h6" fontWeight="medium" color="dark">
                    ROE
                  </MDTypography>
                  <MDTypography variant="h3" fontWeight="bold" color="info">
                    {indicadoresFinancierosData.roe.value.toFixed(1)}%
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Return on Equity
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <MDBox p={2} textAlign="center">
                  <MDTypography variant="h6" fontWeight="medium" color="dark">
                    Liquidez Corriente
                  </MDTypography>
                  <MDTypography variant="h3" fontWeight="bold" color="warning">
                    {indicadoresFinancierosData.liquidez.value.toFixed(1)}x
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Activos / Pasivos
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <MDBox p={2} textAlign="center">
                  <MDTypography variant="h6" fontWeight="medium" color="dark">
                    Endeudamiento
                  </MDTypography>
                  <MDTypography variant="h3" fontWeight="bold" color="error">
                    {indicadoresFinancierosData.endeudamiento.value.toFixed(1)}%
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Pasivos / Activos
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          </Grid>

          {/* Gráficas Principales */}
          <Grid container spacing={3}>
            {/* Evolución Financiera */}
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Evolución Financiera - Ingresos, Gastos y Utilidad
                  </MDTypography>
                  <DefaultLineChart chart={evolucionFinanciera} height="350px" />
                </MDBox>
              </Card>
            </Grid>

            {/* Ingresos por Módulo */}
            <Grid item xs={12} lg={4}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Distribución de Ingresos
                  </MDTypography>
                  <PieChart chart={ingresosPorModulo} height="350px" />
                </MDBox>
              </Card>
            </Grid>

            {/* Gastos por Categoría */}
            <Grid item xs={12} lg={4}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Distribución de Gastos
                  </MDTypography>
                  <PieChart chart={gastosPorCategoria} height="300px" />
                </MDBox>
              </Card>
            </Grid>

            {/* Ventas por Sucursal */}
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Ventas por Sucursal
                  </MDTypography>
                  <VerticalBarChart chart={ventasPorSucursal} height="300px" />
                </MDBox>
              </Card>
            </Grid>

            {/* Rotación de Inventario */}
            <Grid item xs={12} lg={6}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Rotación de Inventario (Días)
                  </MDTypography>
                  <HorizontalBarChart chart={rotacionInventario} height="300px" />
                </MDBox>
              </Card>
            </Grid>

            {/* Eficiencia Operativa */}
            <Grid item xs={12} lg={6}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Eficiencia Operativa (%)
                  </MDTypography>
                  <HorizontalBarChart chart={eficienciaOperativa} height="300px" />
                </MDBox>
              </Card>
            </Grid>

            {/* Tendencia de Clientes */}
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Tendencia de Clientes
                  </MDTypography>
                  <DefaultLineChart chart={tendenciaClientes} height="300px" />
                </MDBox>
              </Card>
            </Grid>
          </Grid>

          {/* Resumen Ejecutivo */}
          <Card sx={{ mt: 3 }}>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Resumen Ejecutivo
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <MDBox mb={2}>
                    <MDTypography variant="button" fontWeight="bold" color="success">
                      Fortalezas:
                    </MDTypography>
                    <MDTypography variant="caption" display="block" mt={1}>
                      • Crecimiento sostenido de ingresos (+15%)
                    </MDTypography>
                    <MDTypography variant="caption" display="block">
                      • Excelente margen de utilidad (27.5%)
                    </MDTypography>
                    <MDTypography variant="caption" display="block">
                      • Alta eficiencia en entregas (94%)
                    </MDTypography>
                    <MDTypography variant="caption" display="block">
                      • Satisfacción del cliente en niveles óptimos (4.6/5)
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDBox mb={2}>
                    <MDTypography variant="button" fontWeight="bold" color="warning">
                      Áreas de Mejora:
                    </MDTypography>
                    <MDTypography variant="caption" display="block" mt={1}>
                      • Reducir días de rotación de inventario (promedio 45.8 días)
                    </MDTypography>
                    <MDTypography variant="caption" display="block">
                      • Optimizar costos operativos (37.6% del total de gastos)
                    </MDTypography>
                    <MDTypography variant="caption" display="block">
                      • Mejorar tasa de retención de clientes (-1.17%)
                    </MDTypography>
                    <MDTypography variant="caption" display="block">
                      • Incrementar pedidos perfectos (actualmente 89%)
                    </MDTypography>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </>
      )}

      {/* Modal de Confirmación de Exportación */}
      <Dialog open={modalExportar.open} onClose={() => setModalExportar({ open: false, tipo: "" })}>
        <DialogTitle
          sx={{
            background:
              modalExportar.tipo === "pdf"
                ? "linear-gradient(195deg, #EF5350, #E53935)"
                : modalExportar.tipo === "excel"
                  ? "linear-gradient(195deg, #66BB6A, #43A047)"
                  : "linear-gradient(195deg, #49a3f1, #1A73E8)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Icon>
            {modalExportar.tipo === "pdf"
              ? "picture_as_pdf"
              : modalExportar.tipo === "excel"
                ? "table_chart"
                : "print"}
          </Icon>
          Confirmar Exportación
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <MDTypography variant="body1">
            {modalExportar.tipo === "pdf" &&
              "¿Desea exportar el dashboard completo a PDF? Se generará un archivo con todos los gráficos e indicadores."}
            {modalExportar.tipo === "excel" &&
              "¿Desea exportar los datos a Excel? Se generará un archivo con todas las métricas y datos tabulados."}
            {modalExportar.tipo === "print" &&
              "¿Desea imprimir el dashboard? Se abrirá el diálogo de impresión del navegador."}
          </MDTypography>
          <MDBox
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "background.default",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <MDTypography variant="caption" display="block" fontWeight="bold" mb={1}>
              Información del reporte:
            </MDTypography>
            <MDTypography variant="caption" display="block">
              • Período: {periodo === "mes" ? "Este Mes" : periodo}
            </MDTypography>
            <MDTypography variant="caption" display="block">
              • Fecha de generación: {new Date().toLocaleDateString("es-ES")}
            </MDTypography>
            {empresa && (
              <MDTypography variant="caption" display="block">
                • Empresa: {empresa}
              </MDTypography>
            )}
            {sucursal && (
              <MDTypography variant="caption" display="block">
                • Sucursal: {sucursal}
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setModalExportar({ open: false, tipo: "" })}
          >
            Cancelar
          </MDButton>
          <MDButton
            variant="gradient"
            color={
              modalExportar.tipo === "pdf"
                ? "error"
                : modalExportar.tipo === "excel"
                  ? "success"
                  : "info"
            }
            onClick={confirmarExportacion}
            disabled={exportando}
          >
            {exportando ? (
              <>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Exportando...
              </>
            ) : (
              <>
                <Icon sx={{ mr: 0.5 }}>
                  {modalExportar.tipo === "pdf"
                    ? "download"
                    : modalExportar.tipo === "excel"
                      ? "download"
                      : "print"}
                </Icon>
                Confirmar
              </>
            )}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.mensaje}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}
